import type { ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon, type IconName } from '@/components/Icon'

/**
 * Состояние экрана словами (дизайн: CompStates — «Нет данных» / «Нет доступа» /
 * «Не настроено» / «Ошибка»).
 *
 * Четыре состояния различаются меткой над заголовком и иконкой, а не только текстом:
 * пустая таблица, запрет и сбой иначе выглядят одинаково — пустотой, — и пустота читается
 * как «данных нет», то есть как штатное состояние. Запрет, показанный пустотой, отправляет
 * в поддержку с работающим запретом.
 */
export type StateKind = 'empty' | 'forbidden' | 'setup' | 'error'

const ICONS: Record<StateKind, IconName> = {
  empty: 'inbox',
  forbidden: 'lock',
  setup: 'settings',
  error: 'alert',
}

const CLASSES: Record<StateKind, string> = {
  empty: 'state',
  forbidden: 'state is-forbidden',
  setup: 'state is-setup',
  error: 'state is-error',
}

export function StateBox({
  kind,
  title,
  description,
  actions,
  code,
}: {
  kind: StateKind
  title: string
  description?: string
  actions?: ReactNode
  /** Код ответа сервера — чтобы поддержка нашла запрос, а не гадала. */
  code?: string | null
}) {
  const { t } = useApp()
  return (
    <div className="state-box">
      <div
        className={CLASSES[kind]}
        role={kind === 'error' || kind === 'forbidden' ? 'alert' : 'status'}
      >
        <span className="state-kind">
          {kind === 'forbidden' && <Icon name="lock" size="xs" />}
          {t(`state.kind.${kind}`)}
        </span>
        <span className="state-ico">
          <Icon name={ICONS[kind]} />
        </span>
        <span className="st">{title}</span>
        {description && <span className="sd">{description}</span>}
        {actions && <span className="sa">{actions}</span>}
        {code && <span className="code">{code}</span>}
      </div>
    </div>
  )
}
